import React from 'react';
import { message, notification } from "antd";
import { BasicRequestDTO, GlobalState, GraphData, PageRequestDTO, ResponseStatus, responseStatusToLowerCase, Section, SectionTileDTO, TileName } from "types/types";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/cs";

import TileTooltip from 'components/tooltip/TileTooltip';

const EMPTY_MESSAGE = React.createElement('span', {style: {color:'red'}}, 'Chybějící data');
export const CHART_COLORS = ['#db0036', '#5c6e8f', '#1677FF', '#0bd900'];
export const FAKE_COMPANY_ID = 9999;

export const isUndefined = (sth: any) => {
    return sth === undefined || sth === null || sth === '';
}

export const getQuarterOffset = () => {
    const now = dayjs().month();
    if ([0, 3, 6, 9].includes(now)) return 1;
    if ([1, 4, 7, 10].includes(now)) return 2;
    return 3;
}

export const getHalfOffset = () => {
    const now = dayjs().month();
    if ([0, 11].includes(now)) return 6;
    if ([1, 10].includes(now)) return 5;
    if ([2, 9].includes(now)) return 4;
    if ([3, 8].includes(now)) return 3;
    if ([4, 7].includes(now)) return 2;
    return 1;
}

export const formatDate = (date?: Dayjs) => {
    if (isUndefined(date)) return;

    return date!.locale('cs').format('MMMM YYYY');
}

export const formatNumber = (percentage: boolean, amount?: number): string => {
    if (isUndefined(amount)) return 'Chybějící data';

    if (percentage) return (amount! * 100).toFixed(0) + ' %';
    return amount!
      .toFixed(0)
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      .concat(" Kč");
}

export const getTextOrEmptyMessage = (sth: any, suffix?: string) => {
    return isUndefined(sth) ? EMPTY_MESSAGE : suffix ? sth + ' ' + suffix : sth;
}

export const getTileName = (tileName?: TileName | Section) => {
    switch (tileName) {
        case 'ACCOUNTING_ASSET': return 'Aktiva - účetní';
        case 'CREDIT': return 'Výše úvěru';
        case 'DEBT_LEVEL': return 'Míra zadlužení';
        case 'EBITDA': return 'EBITDA';
        case 'LOAN': return 'Zapůjčky';
        case 'MARKETABLE_ASSET': return 'Aktiva - tržní';
        case 'COSTS': return 'Náklady';
        default: return 'Chybí překlad';
    };
};

export const convertToDBDate = (datetime: any) => {
    if (isUndefined(datetime)) return '';
    
    return dayjs(datetime).format("YYYY-MM-DD");
}

export const convertToRequestDTO = (picker: GlobalState) => {
    return {companyId: picker.selectedCompanyId, validFrom: convertToDBDate(picker.dates![0]), validTo: convertToDBDate(picker.dates![1])} as BasicRequestDTO;
}

export const convertToPageableDTO = (picker: GlobalState, size: number, page: number) => {
    return {companyId: picker.selectedCompanyId, size: size, page: (page - 1), validFrom: convertToDBDate(picker.dates![0]), validTo: convertToDBDate(picker.dates![1])} as PageRequestDTO;
}

export const createMessage = (type: ResponseStatus, description: string) => {
    const lowerCaseType = responseStatusToLowerCase[type];
    message[lowerCaseType]({
        content: description,
        duration: 10,
        key: description,
        onClick: () => message.destroy(description)
      });
}

export const createNotification = (type: ResponseStatus, description: string, header: string) => {
    const lowerCaseType = responseStatusToLowerCase[type];
    notification[lowerCaseType]({
        duration: 10,
        placement: 'topRight',
        message: header,
        description: description,
        showProgress: true,
        pauseOnHover: true
    });
}

/** GRAPH CONVERT */
export const generateDataSingle = (tileName?: TileName, graphData?: GraphData) => {
    if (isUndefined(graphData) || isUndefined(tileName)) return [];

    const data = Object.entries(graphData!)
    .sort(([key1, value1],[key2, value2]) => key1.localeCompare(key2))
    .map(([key, value]) => ({
        category: key,
        label: getTileName(tileName),
        value: value || 0,
        tileName: tileName,
        valueText: formatNumber(tileName === 'DEBT_LEVEL', value || 0)
    }));

    return data;
}

export const generateDataDouble = (tile?: SectionTileDTO) => {
    if (isUndefined(tile) || isUndefined(tile?.graphData)) return [];
    const data = [{}];

    Object.entries(tile!.graphData)
        .sort(([key1, value1],[key2, value2]) => key1.localeCompare(key2))
        .forEach(([key, value]) => {
            data.push({
                category: 'Plán',
                label: key,
                value: value.second || 0,
                tileName: tile!.tileName,
                valueText: formatNumber(false, value.second)
            });
            data.push({
                category: "Skutečnost",
                label: key,
                value: value.first || 0,
                tileName: tile!.tileName,
                valueText: formatNumber(false, value.first)
            });
        });
    return data;
}

export const getStackChartConfig = (data: any[], specialSetting?: {}) => {
    return {
        ...specialSetting,
        data: data,
        color: CHART_COLORS, // create color schema
        isStack: true, // Make all values part of a single column
        xField: 'label', // X-axis will show the tileName
        yField: 'value', // Y-axis will show the value
        seriesField: 'category', // Different segments will be grouped by 'category'
        colorField: 'category', // Different colors for each category
        label: undefined,
        legend: { // 50% chance to work #sorry not sorry
            color: {
                position: 'top-left'
            }
        },
        tooltip: {
            customContent: (title: string, items: any[]) => React.createElement(TileTooltip, {items: items})
        }
    };
}

export const getGroupChartConfig = (data: any[], specialSetting?: {}) => {
    return {
        ...specialSetting,
        data: data,
        color: CHART_COLORS, // create color schema
        xField: 'label', // X-axis will show the tileName
        yField: 'value', // Y-axis will show the value
        seriesField: 'category', // Different segments will be grouped by 'category'
        colorField: 'category', // Different colors for each category
        isGroup: true, // Make all values part of a single column
        label: undefined,
        point: {
            style: {
                shapeField: 'square',
                sizeField: 4,
            }
        },
        legend: { // 50% chance to work #sorry not sorry
            color: {
                position: 'top-left'
            }
        },
        tooltip: {
            customContent: (title: string, items: any[]) => React.createElement(TileTooltip, {items: items})
        }
    };
}